import {
  CheckCircle2,
  Users,
  Globe,
  Shield,
  Bell,
  BookOpen,
  Check,
  X,
  Minus,
  HelpCircle,
  AlertTriangle,
} from "lucide-react";

export const SECTIONS = [
  {
    id: "consent",
    code: "CON",
    title: "Consent Management",
    icon: CheckCircle2,
    accent: "#6366f1",
    accentLight: "#eef2ff",
    description: "Verify lawful consent collection and withdrawal mechanisms",
    questions: [
      {
        id: "CON01",
        text: "Does your organisation obtain explicit, informed consent before collecting personal data?",
        section: "Section 6",
        severity: "High",
      },
      {
        id: "CON02",
        text: "Is consent sought in clear and plain language (including in regional languages if required)?",
        section: "Section 6(1)",
        severity: "High",
      },
      {
        id: "CON03",
        text: "Is consent obtained separately for each distinct purpose of processing?",
        section: "Section 6(2)",
        severity: "High",
      },
      {
        id: "CON04",
        text: "Does your system allow Data Principals to withdraw consent easily at any time?",
        section: "Section 6(4)",
        severity: "High",
      },
      {
        id: "CON05",
        text: "Do you maintain a timestamped, auditable record of consent given by each individual?",
        section: "Section 6(3)",
        severity: "Medium",
      },
      {
        id: "CON06",
        text: "Is consent re-obtained when the purpose of processing changes?",
        section: "Section 6(3)",
        severity: "Medium",
      },
      {
        id: "CON07",
        text: "Do you have a mechanism to handle consent for children (under 18) with verifiable parental consent?",
        section: "Section 9",
        severity: "High",
      },
    ],
  },
  {
    id: "notice",
    code: "NOT",
    title: "Notice & Purpose Limitation",
    icon: Bell,
    accent: "#0ea5e9",
    accentLight: "#f0f9ff",
    description:
      "Assess transparency in data collection notices and purpose constraints",
    questions: [
      {
        id: "NOT01",
        text: "Do you provide a notice to Data Principals before or at the time of data collection?",
        section: "Section 5",
        severity: "High",
      },
      {
        id: "NOT02",
        text: "Does your privacy notice clearly itemise each category of personal data collected?",
        section: "Section 5(1)(a)",
        severity: "High",
      },
      {
        id: "NOT03",
        text: "Is personal data processed only for the specific purpose for which consent was obtained?",
        section: "Section 6(2)",
        severity: "High",
      },
      {
        id: "NOT04",
        text: "Is your privacy notice easily accessible and written in plain language?",
        section: "Section 5",
        severity: "Medium",
      },
      {
        id: "NOT05",
        text: "Do you notify Data Principals when their data is used for a new or different purpose?",
        section: "Section 6(3)",
        severity: "Medium",
      },
      {
        id: "NOT06",
        text: "Is there a clear legal basis documented for each processing activity?",
        section: "Section 4",
        severity: "High",
      },
    ],
  },
  {
    id: "rights",
    code: "DPR",
    title: "Data Principal Rights",
    icon: Users,
    accent: "#10b981",
    accentLight: "#f0fdf4",
    description: "Evaluate mechanisms to fulfil individual rights under DPDP",
    questions: [
      {
        id: "DPR01",
        text: "Do you have a process to handle access requests from Data Principals (right to access)?",
        section: "Section 11",
        severity: "High",
      },
      {
        id: "DPR02",
        text: "Can Data Principals correct or update inaccurate personal data through a defined process?",
        section: "Section 12(a)",
        severity: "High",
      },
      {
        id: "DPR03",
        text: "Is there a mechanism for Data Principals to request erasure/deletion of their data?",
        section: "Section 12(b)",
        severity: "High",
      },
      {
        id: "DPR04",
        text: "Do you have a grievance redressal officer or mechanism accessible to Data Principals?",
        section: "Section 13",
        severity: "High",
      },
      {
        id: "DPR05",
        text: "Are data requests acknowledged and resolved within prescribed timeframes?",
        section: "Section 11â€“13",
        severity: "Medium",
      },
      {
        id: "DPR06",
        text: "Can Data Principals nominate another person to exercise rights on their behalf?",
        section: "Section 14",
        severity: "Low",
      },
    ],
  },
  {
    id: "fiduciary",
    code: "DFO",
    title: "Data Fiduciary Obligations",
    icon: Shield,
    accent: "#f59e0b",
    accentLight: "#fffbeb",
    description:
      "Review fiduciary duties including DPIA, DPO, and processor contracts",
    questions: [
      {
        id: "DFO01",
        text: "Has your organisation conducted a Data Protection Impact Assessment (DPIA) for high-risk processing?",
        section: "Section 10(2)(a)",
        severity: "High",
      },
      {
        id: "DFO02",
        text: "Have you appointed a Data Protection Officer (DPO) if classified as a Significant Data Fiduciary?",
        section: "Section 10(2)(b)",
        severity: "High",
      },
      {
        id: "DFO03",
        text: "Do you have a periodic auditing mechanism in place for data processing activities?",
        section: "Section 10(2)(c)",
        severity: "Medium",
      },
      {
        id: "DFO04",
        text: "Are Data Processors engaged through written contracts that bind them to DPDP obligations?",
        section: "Section 8(3)",
        severity: "High",
      },
      {
        id: "DFO05",
        text: "Do you ensure Data Processors comply with your data protection obligations?",
        section: "Section 8(2)",
        severity: "High",
      },
      {
        id: "DFO06",
        text: "Is there a process to ensure data accuracy before use?",
        section: "Section 8(5)",
        severity: "Medium",
      },
      {
        id: "DFO07",
        text: "Do you implement appropriate technical and organisational security safeguards?",
        section: "Section 8(4)",
        severity: "High",
      },
    ],
  },
  {
    id: "breach",
    code: "BRM",
    title: "Breach Management",
    icon: AlertTriangle,
    accent: "#ef4444",
    accentLight: "#fff1f2",
    description:
      "Examine incident response readiness and breach notification processes",
    questions: [
      {
        id: "BRM01",
        text: "Do you have a documented data breach response plan and incident response team?",
        section: "Section 8(6)",
        severity: "High",
      },
      {
        id: "BRM02",
        text: "Is there a mechanism to detect and identify personal data breaches promptly?",
        section: "Section 8(6)",
        severity: "High",
      },
      {
        id: "BRM03",
        text: "Do you have a process to notify the Data Protection Board of India (DPBI) of breaches?",
        section: "Section 8(6)",
        severity: "High",
      },
      {
        id: "BRM04",
        text: "Is there a process to notify affected Data Principals in case of a breach?",
        section: "Section 8(6)",
        severity: "High",
      },
      {
        id: "BRM05",
        text: "Are breach records maintained with details of nature, scope, and remedial actions?",
        section: "Section 8(6)",
        severity: "Medium",
      },
      {
        id: "BRM06",
        text: "Have employees been trained to identify and escalate potential data breaches?",
        section: "Section 8(4)",
        severity: "Medium",
      },
    ],
  },
  {
    id: "crossborder",
    code: "CBT",
    title: "Cross-Border Data Transfers",
    icon: Globe,
    accent: "#8b5cf6",
    accentLight: "#f5f3ff",
    description: "Assess controls for international data transfer compliance",
    questions: [
      {
        id: "CBT01",
        text: "Have you identified all cross-border personal data transfers in your data flows?",
        section: "Section 16",
        severity: "High",
      },
      {
        id: "CBT02",
        text: "Do you transfer data only to countries notified as permissible by the Central Government?",
        section: "Section 16(1)",
        severity: "High",
      },
      {
        id: "CBT03",
        text: "Are contractual safeguards in place with overseas recipients of personal data?",
        section: "Section 16",
        severity: "High",
      },
      {
        id: "CBT04",
        text: "Do you maintain records of all international data transfer activities?",
        section: "Section 16",
        severity: "Medium",
      },
    ],
  },
  {
    id: "governance",
    code: "GOV",
    title: "Governance & Record-Keeping",
    icon: BookOpen,
    accent: "#06b6d4",
    accentLight: "#ecfeff",
    description:
      "Review data governance frameworks, policies, and documentation practices",
    questions: [
      {
        id: "GOV01",
        text: "Is there a formal Data Protection Policy approved by senior management?",
        section: "General",
        severity: "High",
      },
      {
        id: "GOV02",
        text: "Have you conducted a comprehensive data mapping and inventory exercise?",
        section: "Section 8(5)",
        severity: "High",
      },
      {
        id: "GOV03",
        text: "Are employees trained on DPDP obligations and data handling practices?",
        section: "Section 8(4)",
        severity: "Medium",
      },
      {
        id: "GOV04",
        text: "Is there a process to manage and update the data inventory when new systems are deployed?",
        section: "Section 8(5)",
        severity: "Medium",
      },
      {
        id: "GOV05",
        text: "Have third-party vendors and partners been assessed for DPDP compliance?",
        section: "Section 8(2)",
        severity: "High",
      },
      {
        id: "GOV06",
        text: "Do you have a retention and deletion schedule for personal data?",
        section: "Section 8(7)",
        severity: "High",
      },
      {
        id: "GOV07",
        text: "Is there an assigned Data Protection Officer or responsible person internally?",
        section: "Section 10",
        severity: "High",
      },
    ],
  },
];

const RESPONSES = ["Yes", "Partially", "No", "N/A"];
export const R_CFG = {
  Yes: {
    icon: Check,
    color: "#047857",
    bg: "#d1fae5",
    border: "#6ee7b7",
    label: "Fully Implemented",
  },
  Partially: {
    icon: Minus,
    color: "#b45309",
    bg: "#fef3c7",
    border: "#fbbf24",
    label: "Work in Progress",
  },
  No: {
    icon: X,
    color: "#b91c1c",
    bg: "#fee2e2",
    border: "#f87171",
    label: "Gap Identified",
  },
  "N/A": {
    icon: HelpCircle,
    color: "#334155",
    bg: "#f1f5f9",
    border: "#94a3b8",
    label: "Not Applicable",
  },
};
